<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useRouter } from 'vue-router';
import { getCrewList, createCrewList } from "@/api";
import { useI18n } from 'vue-i18n';
import CustomModal from "@/components/CustomModal/CustomModal.vue";
import CustomButton from "@/components/Button/Button.vue";

const { t } = useI18n(); // Initialize i18n

const router = useRouter();

function navigateToCrewDetails(uuid) {
    router.push(`/crew-lists/${uuid}`);
}

const crewList = ref([]);
const showAddNewCrewList = ref(false);
const newCrewListName = ref("");
const cardColors = ['#FAFFD8', '#FFF3EB', '#EBFFED'];

onMounted(() => {
    getCrewList()
        .then(data => {
            crewList.value = data.data;
        });
});

function addNewCrewList() {
    if (newCrewListName.value.trim() !== "") {
        const newCrew = {
            uuid: Date.now().toString(),
            name: newCrewListName.value,
            members: []
        };
        crewList.value.push(newCrew);
        newCrewListName.value = "";
        showAddNewCrewList.value = false;
        createCrewList(newCrew.name);
    }
}

</script>

<template>
    <div class="flex flex-col gap-8 container py-12">
        <div class="flex justify-between align-center">
            <div class="responsive-title-3 font-semibold header">{{ $t('crewList.header') }}</div>
            <div class="px-5" v-if="crewList.length !== 0">
                <CustomButton type="black" class="self-end  responsive-body-medium w-fit h-8 btnText" :rounded="true"
                    @click="showAddNewCrewList = true">{{ $t('crewList.addNewList') }}
                </CustomButton>
            </div>
        </div>
        <div v-if="crewList.length === 0"
            class="flex justify-center items-center h-full gap-5 min-h-[400px] alignBoxClass">
            <div class="flex flex-col bg-background-light boxClass justify-between rounded-8xl p-7.5 ">
                <div>
                    <img src="@/assets/icons/svg/Crew_40x40.svg" alt="Crew">
                </div>
                <div class=" responsive-body-large smallText font-semibold">{{ $t('crewList.noCrewList') }}</div>
            </div>
            <div @click="showAddNewCrewList = true"
                class="flex flex-col bg-primary-blue text-white justify-center items-center rounded-8xl p-7.5 gap-1 cursor-pointer boxClass"
                type="primary">
                <img src="@/assets/icons/svg/Add_24x24_white.svg" alt="Plus" width="44" />
                <div class=" responsive-body-large font-semibold text-center">{{ $t('crewList.addNewList') }}</div>
            </div>
        </div>
        <div v-else-if="crewList.length !== 0" class="flex flex-col gap-7">
            <div class="flex flex-row CardsFlexDirection gap-4 ">
                <div v-for="(crew, index) in crewList" :key="crew.uuid" class="">
                    <div class="flex flex-col gap-8 py-6 px-5 rounded-2xl"
                        @click="navigateToCrewDetails(crew.uuid)"
                        :style="{ backgroundColor: cardColors[index % cardColors.length] }">
                        <div class="flex justify-between align-center">
                            <div class="responsive-title-5 font-semibold">
                                {{ crew.name }}
                            </div>
                            <div class="text-sm">
                                <span class="font-semibold">{{ crew.members?.length ?? 0 }}</span> {{
                $t('crewList.members') }}
                            </div>
                        </div>

                        <div class="h-7.5 w-[60%]">
                            <div class="circle-container">
                                <img src="@/assets/home/avatar.png" alt="Image 1">
                                <img src="@/assets/home/avatar.png" alt="Image 2">
                                <img src="@/assets/home/avatar.png" alt="Image 3">
                            </div>
                        </div>
                        <div @click="navigateToCrewDetails(crew.uuid)"
                            class="border border-black px-5 py-2 rounded-12xl cursor-pointer w-fit">
                            <img src="@/assets/icons/svg/Arrow_right_16x16.svg" alt="arrow" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <CustomModal :show="showAddNewCrewList" classes="w-1/6 ">
        <div class="flex flex-col p-6 ">
            <div class="cursor-pointer self-end " type="default" @click="showAddNewCrewList = false">
                <img src="@/assets/icons/svg/Close_16x16.svg" alt="Close" width="18" />
            </div>
            <div class="flex flex-col gap-2 px-9 py-7.5 min-w-[350px]">
                <div class="flex flex-col gap-6">
                    <div class=" responsive-body-large font-semibold">{{ $t('crewList.createNewList') }}</div>
                </div>
                <div class="flex flex-col">
                    <VTextField v-model="newCrewListName" :label="$t('crewList.listNamePlaceholder')" type="name"
                        variant="underlined"></VTextField>
                </div>
                <div class="flex justify-between align-center">
                    <CustomButton type="black" class="self-start  responsive-body-medium w-25 h-13" :rounded="true"
                        @click="addNewCrewList">{{ $t('crewList.createButton') }}
                    </CustomButton>
                </div>
            </div>
        </div>
    </CustomModal>
</template>

<style scoped>
.circle-container {
    display: flex;
    gap: 10px;
    /* Space between circles */
    position: relative;
}

.circle-container img {
    width: 30px;
    /* Adjust the size as needed */
    height: 30px;
    /* Adjust the size as needed */
    border-radius: 50%;
    border: 5px solid white;
    /* White border around the image */
    position: absolute;
}

.circle-container img:nth-child(1) {
    left: 0;
}

.circle-container img:nth-child(2) {
    left: 20px;
    /* Adjust this value to control the overlap */
}

.circle-container img:nth-child(3) {
    left: 40px;
    /* Adjust this value to control the overlap */
}
.border-black{
    border-color: black !important;
}
.CardsFlexDirection >div {
    min-width: 420px;
}

.boxClass {
    min-width: 200px;
    max-width: 220px;
    min-height: 240px;
    max-height: 260px;
}

@media (max-width: 768px) {
    .boxClass {
        min-width: 150px;
        max-width: 180px;
        min-height: 180px;
        max-height: 200px;
    }

    .CardsFlexDirection{
        flex-direction: column !important;
        justify-content:center;
    }
    .CardsFlexDirection > div{
        min-width: 320px !important;
    }
    .btnText{
        font-size: 12px !important;
        min-width: 100px !important;
    }
    .header{
        font-size: 23px;
    }
}
</style>
